import { useMemo } from 'react';
import {
  asBool,
  asNumber,
  asString,
  cloneDoc,
  ensureObject,
  objectRecord,
  type MasterConfigDoc,
} from '../../../lib/master-config-form-utils';
import {
  CheckboxField,
  FieldLabel,
  NumberInput,
  SectionBox,
  TextInput,
} from '../tenant-config-form/FormPrimitives';
import { V2ConfigSliceEditor } from '../config-v2/V2ConfigSliceEditor';

interface MasterConfigFormProps {
  doc: MasterConfigDoc;
  onChange: (doc: MasterConfigDoc) => void;
}

function updateDoc(onChange: (doc: MasterConfigDoc) => void, doc: MasterConfigDoc, mutator: (next: MasterConfigDoc) => void) {
  const next = cloneDoc(doc);
  mutator(next);
  onChange(next);
}

export function MasterConfigForm({ doc, onChange }: MasterConfigFormProps) {
  const monitoring = useMemo(() => objectRecord(doc.monitoring), [doc.monitoring]);
  const monGrafana = objectRecord(monitoring.grafana);
  const monUptime = objectRecord(monitoring.uptime);
  const monUptimeOci = objectRecord(monUptime.oci);
  const monSynthetic = objectRecord(monitoring.synthetic);
  const monAlertmanager = objectRecord(monitoring.alertmanager);
  const monJira = objectRecord(monitoring.jira);
  const monNotif = objectRecord(monitoring.notifications);
  const monTeams = objectRecord(monNotif.teams);

  return (
    <div className="space-y-4 text-sm">
      <V2ConfigSliceEditor doc={doc} onChange={onChange} scope="master" />

      <SectionBox title="Monitoring">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <FieldLabel label="Grafana webhook secret ref">
            <TextInput
              value={asString(monGrafana.webhook_secret_ref)}
              onChange={(v) => updateDoc(onChange, doc, (n) => { ensureObject(ensureObject(n, 'monitoring'), 'grafana').webhook_secret_ref = v; })}
              mono
            />
          </FieldLabel>
          <FieldLabel label="Synthetic base URL">
            <TextInput
              value={asString(monSynthetic.base_url)}
              onChange={(v) => updateDoc(onChange, doc, (n) => { ensureObject(ensureObject(n, 'monitoring'), 'synthetic').base_url = v; })}
              mono
            />
          </FieldLabel>
          <FieldLabel label="Uptime OCI bucket">
            <TextInput
              value={asString(monUptimeOci.bucket)}
              onChange={(v) => updateDoc(onChange, doc, (n) => { ensureObject(ensureObject(ensureObject(n, 'monitoring'), 'uptime'), 'oci').bucket = v; })}
            />
          </FieldLabel>
          <FieldLabel label="Uptime OCI prefix">
            <TextInput
              value={asString(monUptimeOci.prefix)}
              onChange={(v) => updateDoc(onChange, doc, (n) => { ensureObject(ensureObject(ensureObject(n, 'monitoring'), 'uptime'), 'oci').prefix = v || undefined; })}
              mono
            />
          </FieldLabel>
          <FieldLabel label="Uptime timeout (ms)">
            <NumberInput
              value={asNumber(monUptime.timeout_ms, 8000)}
              onChange={(v) => updateDoc(onChange, doc, (n) => { ensureObject(ensureObject(n, 'monitoring'), 'uptime').timeout_ms = v; })}
            />
          </FieldLabel>
          <FieldLabel label="Jira base URL">
            <TextInput
              value={asString(monJira.base_url)}
              onChange={(v) => updateDoc(onChange, doc, (n) => { ensureObject(ensureObject(n, 'monitoring'), 'jira').base_url = v; })}
              mono
            />
          </FieldLabel>
          <FieldLabel label="Jira username">
            <TextInput
              value={asString(monJira.username)}
              onChange={(v) => updateDoc(onChange, doc, (n) => { ensureObject(ensureObject(n, 'monitoring'), 'jira').username = v; })}
            />
          </FieldLabel>
          <FieldLabel label="Jira API token ref">
            <TextInput
              value={asString(monJira.api_token_ref)}
              onChange={(v) => updateDoc(onChange, doc, (n) => { ensureObject(ensureObject(n, 'monitoring'), 'jira').api_token_ref = v; })}
              mono
            />
          </FieldLabel>
          <FieldLabel label="Alertmanager API path">
            <TextInput
              value={asString(monAlertmanager.api_path_active_alerts)}
              onChange={(v) => updateDoc(onChange, doc, (n) => { ensureObject(ensureObject(n, 'monitoring'), 'alertmanager').api_path_active_alerts = v; })}
              mono
            />
          </FieldLabel>
          <FieldLabel label="Alertmanager timeout (ms)">
            <NumberInput
              value={asNumber(monAlertmanager.timeout_ms, 5000)}
              onChange={(v) => updateDoc(onChange, doc, (n) => { ensureObject(ensureObject(n, 'monitoring'), 'alertmanager').timeout_ms = v; })}
            />
          </FieldLabel>
        </div>
        <CheckboxField
          label="Teams notifications enabled"
          checked={asBool(monTeams.enabled)}
          onChange={(checked) => updateDoc(onChange, doc, (n) => {
            ensureObject(ensureObject(ensureObject(n, 'monitoring'), 'notifications'), 'teams').enabled = checked;
          })}
        />
        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-2">
          Jira reporting JQL templates, Teams channel webhooks, and cache policies are preserved — edit those in the YAML tab.
        </p>
      </SectionBox>

      <p className="text-[11px] text-gray-500 dark:text-gray-400 border-t border-gray-200 dark:border-gray-700 pt-3">
        Unlisted sections (cache policies, Jira JQL, Teams channels, etc.) are kept when saving from the form and can be edited in the YAML tab.
      </p>
    </div>
  );
}
